import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, UploadCloud, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import RichText from "../components/RichText";

export default function VoiceSubmit() {
  const { token } = useParams();
  const [invite, setInvite] = useState(undefined); // undefined=loading, null=invalid
  const [author, setAuthor] = useState("");
  const [role, setRole] = useState("");
  const [quote, setQuote] = useState("");
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`).then((r) => setInvite(r.data)).catch(() => setInvite(null));
  }, [token]);

  const isPartner = invite?.type === "partner";
  const field = "w-full bg-white border border-[var(--mf-line)] px-4 py-3 focus:border-[var(--mf-red)] transition-colors";

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/invite/${token}/upload`, fd);
      setImage(data.url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const plain = quote.replace(/<[^>]*>/g, "").trim();
    if (!plain) { toast.error("Bitte geben Sie einen Text ein."); return; }
    setBusy(true);
    try {
      await api.post(`/invite/${token}`, { quote, author, role, image });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--mf-mist)] flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white border border-[var(--mf-line)] p-8">
        <div className="font-head font-bold text-2xl mb-6">MF<span className="text-[var(--mf-red)]"> · </span>MED</div>

        {invite === undefined && <Loader2 className="animate-spin text-[var(--mf-grey)]" />}
        {invite === null && <p className="text-[var(--mf-ink)]">Dieser Link ist ungültig oder abgelaufen.</p>}
        {invite && invite.used && !done && (
          <p className="text-[var(--mf-ink)]">Dieser Link wurde bereits verwendet. Vielen Dank.</p>
        )}

        {invite && !invite.used && !done && (
          <form onSubmit={submit} data-testid="voice-submit-form">
            <span className="mf-eyebrow">{isPartner ? "Partnerstimme" : "Kundenstimme"}</span>
            <h1 className="font-head font-bold text-2xl tracking-tight mt-4 text-[var(--mf-ink)]">
              {invite.name ? `Guten Tag, ${invite.name}` : "Ihre Rückmeldung"}
            </h1>
            <p className="text-[var(--mf-grey)] mt-2 text-sm">Ihr Text erscheint nach Freigabe auf unserer Website. Ein Bild oder Logo können Sie optional ergänzen.</p>

            <div className="mt-6 space-y-4">
              <input data-testid="voice-author" className={field} placeholder={isPartner ? "Firma / Partner" : "Name / Einrichtung"} value={author} onChange={(e) => setAuthor(e.target.value)} />
              <input data-testid="voice-role" className={field} placeholder={isPartner ? "Hersteller / Software" : "Ort"} value={role} onChange={(e) => setRole(e.target.value)} />
              <RichText label="Ihr Text" value={quote} onChange={setQuote} />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--mf-grey)] mb-1.5">Bild / Logo (optional)</label>
                <div className="flex items-center gap-3">
                  {image ? <img src={image} alt="Vorschau" className="h-12 w-auto object-contain border border-[var(--mf-line)] p-1" /> : null}
                  <label className="text-xs inline-flex items-center gap-1.5 border border-[var(--mf-line)] px-3 py-2 hover:bg-[var(--mf-mist)] cursor-pointer">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />} Hochladen
                    <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="voice-image-input" />
                  </label>
                  {image && <button type="button" onClick={() => setImage("")} className="text-xs text-[var(--mf-red)] inline-flex items-center gap-1"><X size={12} /> Entfernen</button>}
                </div>
              </div>
              <button data-testid="voice-submit" disabled={busy} className="mf-cta w-full disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Absenden
              </button>
            </div>
          </form>
        )}

        {done && (
          <div data-testid="voice-success" className="text-center py-4">
            <CheckCircle2 className="text-[var(--mf-red)] mx-auto" size={44} />
            <p className="mt-4 font-head font-semibold text-lg text-[var(--mf-ink)]">Vielen Dank</p>
            <p className="text-sm text-[var(--mf-grey)] mt-1">Ihre Rückmeldung ist eingegangen und wird nach Prüfung veröffentlicht.</p>
          </div>
        )}
      </div>
    </div>
  );
}
